import { useState } from "react";
import type { OverlayType } from "../hooks/useOverlayTypes";
import { API_BASE } from "../config";
import { DynamicField } from "../components/ui/common/DynamicField";
import { ApiService } from "../services/ApiService";
import { getColor } from "./colorUtils";

const OVERLAY_SWATCH_FALLBACK = "var(--chart-series-1)";
const OVERLAY_INPUT_FALLBACK = "#2c7be5";

interface Props {
    overlayTypes: OverlayType[];
    overlays: any[];
    onAdd: (newOverlay: any) => void;
    onUpdate: (idx: number, updated: any) => void;
    onRemove: (idx: number) => void;
    baseData: { time: string; value: number }[];
    globalStart?: string | null;
    globalEnd?: string | null;
}

export function OverlayManager({ overlayTypes, overlays, onAdd, onUpdate, onRemove, baseData, globalStart, globalEnd }: Props) {
    const [modalOpen, setModalOpen] = useState(false);
    const [editingIdx, setEditingIdx] = useState<number | null>(null);
    const [selectedType, setSelectedType] = useState<OverlayType | null>(null);
    const [params, setParams] = useState<Record<string, any>>({});

    const [editParams, setEditParams] = useState<Record<string, any>>({});


    function handleTypeChange(typeId: string) {
        const type = overlayTypes.find(t => t.id === typeId);
        setSelectedType(type || null);

        if (type) {
            const defVals: Record<string, any> = {};
            type.properties?.forEach(p => {
                if (p.default !== undefined) defVals[p.key] = p.default;
            });
            setParams(defVals);
        } else {
            setParams({});
        }
    }


    async function saveOverlay() {
        if (!selectedType) return;
        const res = await fetch(`${API_BASE}/overlay/calculate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ overlayId: selectedType.id, baseData, params }),
        }).then(r => r.json());

        const overlayObj = {
            id: selectedType.id,
            label: selectedType.label,
            params,
            result: res,
            name: selectedType.label,              // user can rename
            color: getColor(overlays.length + 1)
        };


        if (editingIdx !== null) {
            onUpdate(editingIdx, overlayObj);
        } else {
            onAdd(overlayObj);
        }
        setModalOpen(false);
    }


    const [openIdx, setOpenIdx] = useState<number | null>(null);

    function toggleOpen(idx: number) {
        if (openIdx === idx) {
            setOpenIdx(null);
            return;
        }
        setOpenIdx(idx);
        setEditParams({ ...overlays[idx].params }); // copy params for editing
    }

    return (
        <div>
            <div style={{ marginBottom: "0.5rem" }}>
                <button onClick={() => { setSelectedType(null); setParams({}); setEditingIdx(null); setModalOpen(true); }}>
                    ➕ Add Overlay
                </button>

            </div>

            {overlays.length > 0 && (
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {overlays.map((o, idx) => {
                        const isOpen = openIdx === idx;
                        return (
                            <li key={idx} style={{ marginBottom: "0.25rem", border: "1px solid var(--surface-glass-border)", borderRadius: 6 }}>
                                {/* --- Header row --- */}
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "0.25rem 0.5rem",
                                        cursor: "pointer",
                                        background: "var(--bg-card)",
                                    }}
                                    onClick={() => toggleOpen(idx)}
                                >
                                    <span
                                        style={{
                                            width: 16,
                                            height: 16,
                                            background: o.color || OVERLAY_SWATCH_FALLBACK,
                                            borderRadius: "50%",
                                            marginRight: 8,
                                        }}
                                    />
                                    <span style={{ flexGrow: 1, fontWeight: "500" }}>{o.name}</span>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRemove(idx);
                                        }}
                                        style={{ color: "var(--accent-danger)", border: "none", background: "transparent", cursor: "pointer" }}
                                    >
                                        ✕
                                    </button>
                                </div>

                                {/* --- Expanded content --- */}
                                {isOpen && (
                                    <div style={{ padding: "0.5rem 0.75rem", background: "var(--bg-panel)" }}>
                                        {/* Rename */}
                                        <div style={{ marginBottom: "0.5rem" }}>
                                            <label style={{ display: "block", fontSize: "0.8rem", marginBottom: "0.25rem" }}>
                                                Name
                                            </label>
                                            <input
                                                style={{ width: "100%" }}
                                                value={o.name}
                                                onChange={(e) => onUpdate(idx, { ...o, name: e.target.value })}
                                            />
                                        </div>

                                        {/* Color */}
                                        <div style={{ marginBottom: "0.5rem" }}>
                                            <label style={{ display: "block", fontSize: "0.8rem", marginBottom: "0.25rem" }}>
                                                Color
                                            </label>
                                            <input
                                                type="color"
                                                defaultValue={o.color?.startsWith("var(") ? OVERLAY_INPUT_FALLBACK : (o.color || OVERLAY_INPUT_FALLBACK)}
                                                onBlur={(e) =>
                                                    onUpdate(idx, { ...o, color: e.target.value })
                                                }
                                                onMouseUp={(e) =>
                                                    onUpdate(idx, { ...o, color: e.currentTarget.value })
                                                }
                                            />

                                        </div>

                                        {/* Parameters */}
                                        {overlayTypes
                                            .find((t) => t.id === o.id)
                                            ?.properties?.map((input) => (
                                                <DynamicField
                                                    key={input.key}
                                                    spec={input}
                                                    context={{}}
                                                    getOptions={() => Promise.resolve([])}
                                                    value={editParams[input.key] ?? input.default ?? ""}
                                                    onChange={(v) => {
                                                        setEditParams((prev) => ({ ...prev, [input.key]: v }));
                                                    }}
                                                />
                                            ))}

                                        {/* Save button */}
                                        <div style={{ marginTop: "0.5rem" }}>
                                            <button
                                                onClick={async () => {
                                                    const updatedParams = {
                                                        ...editParams,
                                                        ...(o.id === 'strategy-comparison' ? { start: globalStart, end: globalEnd } : {})
                                                    };
                                                    const res = await ApiService.calculateOverlay({
                                                        type_id: o.id,
                                                        params: updatedParams,
                                                        data: baseData
                                                    });
                                                    onUpdate(idx, { ...o, params: updatedParams, result: res });
                                                    setOpenIdx(null);
                                                }}
                                            >
                                                💾 Apply / Recalculate
                                            </button>
                                            <button onClick={() => setOpenIdx(null)} style={{ marginLeft: 8 }}>✖ Cancel</button>
                                        </div>

                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            {modalOpen && (
                <div style={modalStyle}>
                    <div style={modalContent}>
                        <h3>{editingIdx !== null ? "Edit Overlay" : "Add Overlay"}</h3>

                        <label>Overlay Type:</label>
                        <select
                            value={selectedType?.id || ""}
                            onChange={(e) => handleTypeChange(e.target.value)}
                        >
                            <option value="">Select overlay</option>
                            {overlayTypes.map(t => (
                                <option key={t.id} value={t.id}>{t.label}</option>
                            ))}
                        </select>

                        {selectedType && selectedType.properties.map(input => (
                            <DynamicField
                                key={input.key}
                                spec={input}
                                context={{}}
                                getOptions={() => Promise.resolve([])}
                                value={params[input.key] ?? input.default ?? ""}
                                onChange={v => setParams(prev => ({ ...prev, [input.key]: v }))}
                            />
                        ))}

                        <div style={{ marginTop: "1rem" }}>
                            <button onClick={saveOverlay} style={{ marginRight: "0.5rem" }}>✅ Save</button>
                            <button onClick={() => setModalOpen(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );

}

const modalStyle: React.CSSProperties = {
    position: "fixed",
    top: 0, left: 0, width: "100%", height: "100%",
    background: "var(--overlay-backdrop)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999
};
const modalContent: React.CSSProperties = {
    background: "var(--bg-panel)",
    padding: "1rem",
    borderRadius: "8px",
    width: "400px",
    border: "1px solid var(--surface-glass-border)",
    color: "var(--text-primary)"
};
